import React from 'react'
import invariant from 'invariant'
import isFunction from 'lodash/isFunction'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import wrappedDisplayName from '../../utils/wrapperDisplayName'

/**
 * check if has resource
 *
 * @param {Any} resource - resource prop to check
 * @return {Boolean} ture if has resource
 */
const checkResource = resource => !!resource

/**
 * HOC for create components with resources
 *
 * @param {Object} options - options to create component
 * @param {(state, props) => Any} options.resourceSelector - redux selector to get the resource
 * @param {(Any, props) => Boolean} [options.hasResource=checkResource] - function to check if has resource
 * @param {String} [options.resourceProp='resource'] - resource prop name
 * @param {String} [options.displayName='withResource'] - hoc wrapper displayName
 * @param {StatelessComponent|React.Component} [options.FallbackComponent] - component to render if has not resource
 * @return {(StatelessComponent|React.Component) => StatelessComponent} function to generate the decorated component
 */
export default function withResource(options = {}) {
  const {
    resourceSelector,
    hasResource = checkResource,
    resourceProp = 'resource',
    displayName = 'withResource',
    FallbackComponent
  } = options

  invariant(
    isFunction(resourceSelector),
    'Please provide a `resourceSelector` function to withResource hoc'
  )

  return WrappedComponent => {
    function WithResource(props) {
      if (!hasResource(props[resourceProp], props)) {
        return FallbackComponent ? <FallbackComponent {...props} /> : null
      }
      return <WrappedComponent {...props} />
    }

    WithResource.displayName = wrappedDisplayName(displayName, WrappedComponent)

    const mapStateToProps = createStructuredSelector({
      [resourceProp]: resourceSelector
    })
    const Connected = connect(mapStateToProps)(WithResource)

    return hoistNonReactStatics(Connected, WrappedComponent)
  }
}
